import { useEffect, useMemo, useRef, useState } from 'react';

import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  useChangeEmbryoOwnerMutation,
  useGetDataEntryQuery,
  useGetRecipsInQueueQuery,
  usePcOpenMutation,
  usePcPregMutation,
} from '@/app/services/inClinic';
import { EmbryoLink } from '@/components/common/links';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useSites } from '@/services/sites';

import { useImplantSession } from '../../hooks/useImplantSession';
import { useInClinicBasePath } from '../../inClinicPath';
import type { FreshEmbryoFilters } from '../../types';
import { getFriendlyError } from '../../utils/errorMessages';
import { BillingAccountSearchDialog } from '../dialogs/BillingAccountSearchDialog';
import { HeatNotUsedDialog } from '../dialogs/HeatNotUsedDialog';
import { ImplantEmbryoDialog } from '../dialogs/ImplantEmbryoDialog';
import { OwnerSearchDialog } from '../dialogs/OwnerSearchDialog';
import { ImplantToolbar } from '../ImplantToolbar';
import { getClOptions } from './clOptions';
import { getImplantBlockedReason } from './implantGate';
import {
  getOwnerOptionLabel,
  getSelectedOwner,
  resolveBillingAccount,
} from './ownerDisplay';
import { RecipProcHistory } from './RecipProcHistory';
import { buildHomeUrl, buildSessionParams } from './sessionUrl';

const dash = (s?: string | null) => (s && s.length > 0 ? s : '—');

export function InClinicDataEntry() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const session = useImplantSession();
  const isEstrusSyncEnabled = useFeatureFlag('Chute_Implant_Estrus_Sync');
  const linkToEmbryoModule = useFeatureFlag('Link_To_Embryo_Module');
  const useReadableErrors = useFeatureFlag('Replace_tech_error_with_readable');
  const preserveEmbryoFilters = useFeatureFlag('Implant_Preserve_Purch_Preg');
  const allowOtherLabSiteFilter = useFeatureFlag(
    'Implant_Find_From_Other_Lab_Without_Loca_Change',
  );
  const billingDefaultEnabled = useFeatureFlag('In_Clinic_Billing_Default');
  const hidePartnership = useFeatureFlag('In_Clinic_Hide_Partnership');
  const autoThawMethod = useFeatureFlag('Auto_Thaw_Method');
  const basePath = useInClinicBasePath();
  // TODO: replace with hasPermission(currentUser, MODULES.CHUTE_IMPLANT_WRITE) once the permission exists
  const canWrite = true;

  const embryoId = searchParams.get('embryoId') || '';
  const recipName = searchParams.get('recipName') || '';
  const viewMode =
    (searchParams.get('viewMode') as 'fresh' | 'frozen') || 'fresh';
  const embryoFilters = preserveEmbryoFilters
    ? (searchParams.getAll('embryoType') as FreshEmbryoFilters)
    : [];
  const fertSiteId = allowOtherLabSiteFilter
    ? searchParams.get('fertSiteId') || ''
    : '';
  const yard = session.yardId;
  const chute = session.chuteNumber;

  const sessionParams = () =>
    buildSessionParams(session, viewMode, embryoFilters, fertSiteId);

  const navHome = () => {
    navigate(
      buildHomeUrl(session, viewMode, embryoFilters, fertSiteId, basePath),
    );
  };

  const {
    data: entry,
    isLoading,
    refetch,
  } = useGetDataEntryQuery(
    { embryoId, yard, chute, recipName },
    { skip: !embryoId || !yard },
  );

  const { data: sitesRaw, isLoading: sitesLoading } = useSites({
    filter: 'fertilization',
  });
  const sites = sitesRaw ?? [];

  const { data: recipsInQueue = [] } = useGetRecipsInQueueQuery(
    { yardId: yard, chute },
    { skip: !yard },
  );

  const [pcOpen, { isLoading: pcOpenLoading }] = usePcOpenMutation();
  const [pcPreg, { isLoading: pcPregLoading }] = usePcPregMutation();
  const [changeOwner] = useChangeEmbryoOwnerMutation();

  const toast = useRef<Toast>(null);
  const [cl, setCl] = useState('');
  const [thawMethod, setThawMethod] = useState('');
  const thawMethodOptions = useMemo(
    () =>
      (entry?.thawMethodOptions ?? []).map((option) => ({
        label: option.label,
        value: String(option.value),
      })),
    [entry],
  );
  const [usePartnership, setUsePartnership] = useState(false);
  const [showImplantDialog, setShowImplantDialog] = useState(false);
  const [showHnuDialog, setShowHnuDialog] = useState(false);
  const [showOwnerSearch, setShowOwnerSearch] = useState(false);
  const [billingAccountId, setBillingAccountId] = useState('');
  const [billingAccountName, setBillingAccountName] = useState('');
  const [showBillingAccountSearch, setShowBillingAccountSearch] =
    useState(false);

  const billingSynced = useRef<{ recordKey: string; ownerId: string } | null>(
    null,
  );
  const thawMethodSynced = useRef<string | null>(null);

  const handleBack = () => navHome();

  useEffect(() => {
    if (!entry) return;
    const recordKey = `${entry.embryoId}|${entry.recipQueueId}|${autoThawMethod}`;
    if (autoThawMethod && thawMethodSynced.current === recordKey) return;
    thawMethodSynced.current = recordKey;
    setThawMethod(
      autoThawMethod
        ? entry.thawMethodId != null
          ? String(entry.thawMethodId)
          : ''
        : entry.thawMethod || '',
    );
  }, [entry, autoThawMethod]);

  useEffect(() => {
    if (entry) {
      if (!billingDefaultEnabled) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setBillingAccountId(entry.billingAccountId || '');
        setBillingAccountName(entry.billingAccountName || '');
        return;
      }
      const recordKey = `${embryoId}|${recipName}`;
      const synced = billingSynced.current;
      const next = resolveBillingAccount({
        entry,
        current: { id: billingAccountId, name: billingAccountName },
        syncedOwnerId: synced?.recordKey === recordKey ? synced.ownerId : null,
      });
      billingSynced.current = {
        recordKey,
        ownerId: getSelectedOwner(entry)?.ownerId ?? '',
      };
      setBillingAccountId(next.id);
      setBillingAccountName(next.name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry, billingDefaultEnabled, embryoId, recipName]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'F4') {
        e.preventDefault();
        navHome();
      }
    };
    window.addEventListener('keyup', onKey);
    return () => window.removeEventListener('keyup', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    navigate,
    basePath,
    session.siteId,
    session.yardId,
    session.vetId,
    session.techId,
    session.chuteNumber,
    session.impDate,
  ]);

  const showError = (summary: string, detail: string) =>
    toast.current?.show({ severity: 'error', summary, detail, life: 4000 });

  const handlePcOpen = async () => {
    if (!entry || !entry.recipId) return;
    const result = await pcOpen({
      recipId: entry.recipId,
      yardId: yard,
      vetId: session.vetId,
      techId: session.techId,
    });
    if ('error' in result) {
      showError(
        'PC Open failed',
        getFriendlyError(
          result.error as { status: number | string; data?: string },
          'PC Open',
          useReadableErrors,
        ),
      );
      return;
    }
    toast.current?.show({
      severity: 'success',
      summary: 'PC Open recorded',
      life: 2000,
    });
    refetch();
  };

  const runPcPreg = async () => {
    if (!entry || !entry.recipId) return;
    const result = await pcPreg({
      recipId: entry.recipId,
      yardId: yard,
      vetId: session.vetId,
      techId: session.techId,
    });
    if ('error' in result) {
      showError(
        'PC Preg failed',
        getFriendlyError(
          result.error as { status: number | string; data?: string },
          'PC Preg',
          useReadableErrors,
        ),
      );
      return;
    }
    toast.current?.show({
      severity: 'success',
      summary: 'PC Preg recorded',
      life: 2000,
    });
    refetch();
  };

  const handlePcPreg = () => {
    confirmDialog({
      message: 'You are about to mark this Recip as Pregnant. Are you sure?',
      header: 'Confirm Pregnancy Check',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: 'Yes, Mark as Pregnant',
      rejectLabel: 'Cancel',
      accept: runPcPreg,
    });
  };

  const handleNext = () => {
    const currentIndex = recipsInQueue.findIndex(
      (r) => r.tag === recipName || r.clientRecipTag === recipName,
    );
    const nextRecip = recipsInQueue[currentIndex + 1];
    if (nextRecip) {
      const params = sessionParams();
      params.set('embryoId', embryoId);
      params.set('recipName', nextRecip.tag || nextRecip.clientRecipTag || '');
      params.set('recipQueueId', nextRecip.queueId.toString());
      navigate(`${basePath}/data-entry?${params.toString()}`);
    } else {
      navHome();
    }
  };

  const handleOwnerChange = async (newOwnerId: string) => {
    if (!entry) return;
    const result = await changeOwner({ embryoId: entry.embryoId, newOwnerId });
    if ('error' in result) {
      showError(
        'Owner change failed',
        getFriendlyError(
          result.error as { status: number | string; data?: string },
          'Owner change',
          useReadableErrors,
        ),
      );
      return;
    }
    refetch();
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4 p-4">
        <div className="flex items-center gap-2">
          <div className="h-9 w-16 animate-pulse rounded bg-gray-200" />
          <div className="h-7 w-48 animate-pulse rounded bg-gray-200" />
        </div>
        <div className="h-16 animate-pulse rounded-lg bg-gray-200" />
        <div className="h-40 animate-pulse rounded-lg bg-gray-200" />
        <div className="h-40 animate-pulse rounded-lg bg-gray-200" />
      </div>
    );
  }

  if (!entry) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 p-8">
        <span className="text-muted-foreground">
          No data entry record found
        </span>
        <Button onClick={handleBack}>Back</Button>
      </div>
    );
  }

  const recipStatusColor =
    entry.recipStatus === 'Implanted'
      ? 'bg-yellow-100 text-yellow-800'
      : entry.recipStatus === 'Pregnant'
        ? 'bg-red-100 text-red-800'
        : entry.recipStatus === 'Open'
          ? 'bg-green-100 text-green-800'
          : 'bg-gray-100 text-gray-800';

  const isImplanted = entry.recipStatus === 'Implanted';
  const isPregnant = entry.recipStatus === 'Pregnant';
  const anyLoading = pcOpenLoading || pcPregLoading;
  const writeDisabledTitle = canWrite
    ? undefined
    : 'You lack CHUTE_IMPLANT.WRITE permission';
  const siteName = sites.find((site) => site.id === entry.siteId)?.name;

  const implantBlockedReason = getImplantBlockedReason({
    canWrite,
    vetId: session.vetId,
    techId: session.techId,
    cl,
    isPregnant,
    estrusSync: isEstrusSyncEnabled ? entry.recipSync : null,
  });
  const canImplant = implantBlockedReason === null;

  return (
    <div className="flex flex-col gap-4 p-4">
      <Toast ref={toast} />
      <ConfirmDialog />
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={handleBack}>
          Back
        </Button>
        <h1 className="text-lg font-semibold sm:text-2xl">
          Data Entry - {recipName}
        </h1>
        <span className="text-muted-foreground hidden text-xs sm:inline">
          (F4 to return)
        </span>
      </div>

      <ImplantToolbar session={session} readonly />

      <div className="rounded-lg border p-4">
        <h2 className="mb-3 text-sm font-semibold">Embryo Info</h2>
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
          <div>
            <span className="text-muted-foreground">Mating:</span>{' '}
            {linkToEmbryoModule && entry.mating ? (
              <EmbryoLink
                embryoId={entry.embryoId}
                embryoName={entry.mating}
                isOutside={entry.isOutsideEmbryo}
                openInNewTab
              />
            ) : (
              <span className="font-medium">{dash(entry.mating)}</span>
            )}
          </div>
          <div>
            <span className="text-muted-foreground">Stage:</span>{' '}
            <span className="font-medium">{dash(entry.stage)}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Grade:</span>{' '}
            <span className="font-medium">{dash(entry.grade)}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Straw #:</span>{' '}
            <span className="font-medium">
              {dash(entry.freshStrawNum || entry.strawNum)}
            </span>
          </div>
          <div>
            <span className="text-muted-foreground">Tag #:</span>{' '}
            <span className="font-medium">{dash(entry.embryoTagNumber)}</span>
          </div>
          {!entry.isFresh && autoThawMethod && (
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Thaw Method:</span>
              <Select
                value={thawMethod}
                onChange={(val) => setThawMethod(String(val))}
              >
                <SelectTrigger className="h-7 w-40" size="sm">
                  <SelectValue placeholder="(none)">
                    {(value) =>
                      thawMethodOptions.find((o) => o.value === value)?.label ??
                      '(none)'
                    }
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {thawMethodOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {!entry.isFresh && !autoThawMethod && (
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Thaw Method:</span>
              <Input
                className="h-7 w-32"
                value={thawMethod}
                onChange={(e) => setThawMethod(e.target.value)}
                placeholder="Thaw method..."
              />
            </div>
          )}
          {entry.isFresh && (
            <div>
              <span className="text-muted-foreground">Thaw Method:</span>{' '}
              <span className="font-medium">—</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Owner:</span>
            <Select
              value={getSelectedOwner(entry)?.ownerId}
              onChange={(val) => handleOwnerChange(String(val))}
            >
              <SelectTrigger className="h-7 w-40" size="sm">
                <SelectValue placeholder="(no owner)">
                  {(value) => {
                    const selected = entry.embryoOwners.find(
                      (o) => o.ownerId === value,
                    );
                    return selected
                      ? getOwnerOptionLabel(selected)
                      : '(no owner)';
                  }}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {entry.embryoOwners.map((owner) => (
                  <SelectItem key={owner.ownerId} value={owner.ownerId}>
                    {getOwnerOptionLabel(owner)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="xs"
              variant="outline"
              onClick={() => setShowOwnerSearch(true)}
              disabled={!canWrite}
              title={writeDisabledTitle}
            >
              Search…
            </Button>
          </div>
          {allowOtherLabSiteFilter && entry.isFresh && (
            <div>
              <span className="text-muted-foreground">Site:</span>{' '}
              <span className="font-medium">
                {sitesLoading ? 'Loading...' : dash(siteName)}
              </span>
            </div>
          )}

          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">
              Implant Billing Account <span className="text-red-500">*</span>:
            </span>
            <span className="font-medium">
              {billingAccountName || (
                <span className="text-muted-foreground italic">None</span>
              )}
            </span>
            <Button
              size="xs"
              variant="outline"
              onClick={() => setShowBillingAccountSearch(true)}
              disabled={!canWrite}
              title={writeDisabledTitle}
            >
              Search…
            </Button>
            {billingAccountId && (
              <Button
                size="xs"
                variant="ghost"
                onClick={() => {
                  setBillingAccountId('');
                  setBillingAccountName('');
                }}
                disabled={!canWrite}
                title={writeDisabledTitle}
              >
                Clear
              </Button>
            )}
          </div>
        </div>
      </div>

      {!hidePartnership && entry.partnership && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
          <h2 className="mb-2 text-sm font-semibold text-blue-900">
            Partnership
          </h2>
          <p className="text-xs text-blue-800">{entry.partnershipAccounts}</p>
          <label className="mt-2 flex items-center gap-2 text-sm text-blue-900">
            <Checkbox
              checked={usePartnership}
              onCheckedChange={(checked) => setUsePartnership(!!checked)}
            />
            Use Partnerships
          </label>
        </div>
      )}

      <div className="rounded-lg border p-4">
        <h2 className="mb-3 text-sm font-semibold">Recipient Info</h2>
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
          <div>
            <span className="text-muted-foreground">Name:</span>{' '}
            <span className="font-medium">{dash(entry.recipName)}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Status:</span>{' '}
            <span
              className={`rounded px-2 py-0.5 text-xs font-medium ${recipStatusColor}`}
            >
              {entry.recipStatus || (entry.haulIn ? 'Haul-In' : '—')}
            </span>
          </div>
          <div>
            <span className="text-muted-foreground">ES#:</span>{' '}
            <span className="font-medium">{dash(entry.recipSync)}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Type:</span>{' '}
            <span className="font-medium">{dash(entry.recipType)}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">CL:</span>
            <Select value={cl} onChange={(val) => setCl(String(val))}>
              <SelectTrigger className="h-7 w-20" size="sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {getClOptions(cl).map((opt) => (
                  <SelectItem key={opt} value={opt}>
                    {opt}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            onClick={() => setShowImplantDialog(true)}
            disabled={!canImplant || anyLoading}
            title={implantBlockedReason ?? undefined}
          >
            Implant
          </Button>
          <Button
            variant="outline"
            onClick={handlePcOpen}
            disabled={!isImplanted || anyLoading || !canWrite}
            title={writeDisabledTitle}
          >
            {pcOpenLoading ? 'Processing...' : 'PC Open'}
          </Button>
          <Button
            variant="outline"
            onClick={handlePcPreg}
            disabled={!isImplanted || anyLoading || !canWrite}
            title={writeDisabledTitle}
          >
            {pcPregLoading ? 'Processing...' : 'PC Preg'}
          </Button>
          <Button
            variant="destructive"
            onClick={() => setShowHnuDialog(true)}
            disabled={anyLoading || !canWrite}
            title={writeDisabledTitle}
          >
            Heat Not Used
          </Button>
          <Button
            variant="secondary"
            onClick={handleNext}
            disabled={anyLoading}
          >
            Return to Home
          </Button>
        </div>
      </div>

      <RecipProcHistory recipId={entry.recipId} />

      <ImplantEmbryoDialog
        open={showImplantDialog}
        onOpenChange={setShowImplantDialog}
        entry={entry}
        cl={cl}
        session={session}
        canWrite={canWrite}
        thawMethod={thawMethod}
        usePartnership={hidePartnership ? false : usePartnership}
        billingAccountId={billingAccountId}
        billingAccountName={billingAccountName}
        onSuccess={() => {
          setShowImplantDialog(false);
          toast.current?.show({
            severity: 'success',
            summary: 'Embryo implanted',
            life: 2000,
          });
          navHome();
        }}
      />

      <HeatNotUsedDialog
        open={showHnuDialog}
        onOpenChange={setShowHnuDialog}
        target={{
          recipId: entry.recipId,
          recipQueueId: entry.recipQueueId,
          recipStatus: entry.recipStatus,
        }}
        session={session}
        canWrite={canWrite}
        onSuccess={() => {
          setShowHnuDialog(false);
          toast.current?.show({
            severity: 'success',
            summary: 'Heat Not Used recorded',
            life: 2000,
          });
          navHome();
        }}
      />

      <OwnerSearchDialog
        open={showOwnerSearch}
        onOpenChange={setShowOwnerSearch}
        onSelect={(ownerId) => handleOwnerChange(ownerId)}
      />

      <BillingAccountSearchDialog
        open={showBillingAccountSearch}
        onOpenChange={setShowBillingAccountSearch}
        onSelect={(id, name) => {
          setBillingAccountId(id);
          setBillingAccountName(name);
        }}
      />
    </div>
  );
}
